import * as React from 'react';

import type {CoreEntityType} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {FormField} from 'shells/form/form-field';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {RadioButton} from 'shells/radio/radio-button';
import {RadioGroup} from 'shells/radio/radio-group';
import {TextField} from 'shells/textfield/textfield';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';
import {Body} from 'shells/typography/body';

import type {NavItemObj} from '../entity-list-sidebar/types';

type Props = {
    navItem: NavItemObj;
    entityType: CoreEntityType;
    onUpdateFilter: (
        name: string,
        description: string | null | undefined,
        isShared: boolean
    ) => void;
    onCreateFilter: (
        name: string,
        description: string | null | undefined,
        isShared: boolean
    ) => void;
    onUpdateAudience: (name: string, description?: string | null | undefined) => void;
    onCreateAudience: (name: string, description?: string | null | undefined) => void;
    onClose: () => void;
    isSaving: boolean;
    isCopying: boolean;
    canAccessMarketing: boolean;
    isLimitedMarketingUser: boolean;
};

export function SaveListModalForm(props: Props) {
    const [name, setName] = React.useState(props.navItem.id ? props.navItem.name : '');
    const [description, setDescription] = React.useState(props.navItem.description);
    const [listType, setListType] = React.useState(props.navItem.type);
    const [isShared, setIsShared] = React.useState(Boolean(props.navItem.isShared));

    const {progressTour} = useNutshellTour();

    const onCopy = () => {
        if (listType === 'list') {
            props.onCreateFilter(name, description, isShared);
        } else {
            props.onCreateAudience(name, description);
        }
    };

    const onSave = () => {
        if (listType === 'list') {
            if (props.navItem.id) {
                props.onUpdateFilter(name, description, isShared);
            } else {
                progressTour();
                props.onCreateFilter(name, description, isShared);
            }
        } else if (listType === 'audience') {
            if (props.navItem.id) {
                props.onUpdateAudience(name, description);
            } else {
                props.onCreateAudience(name, description);
            }
        }
    };

    return (
        <>
            <FormField innerId='name' label='Name' isRequired={true}>
                <TextField
                    id='name'
                    // @ts-expect-error
                    name='name'
                    size='normal'
                    placeholder='Name this list or audience'
                    onChange={(val) => {
                        setName(val);
                    }}
                    value={name || ''}
                    focusOnMount={true}
                    selectOnFocus={true}
                    ignorePasswordAutoFill={true}
                />
            </FormField>
            <FormField innerId='description' label='Description' isRequired={false}>
                <TextField
                    id='description'
                    // @ts-expect-error
                    name='description'
                    size='normal'
                    placeholder='Describe this list or audience'
                    onChange={(val) => {
                        setDescription(val);
                    }}
                    value={description || ''}
                    ignorePasswordAutoFill={true}
                />
            </FormField>
            {props.entityType === 'contacts' && !props.isLimitedMarketingUser && (
                <FormField innerId='listType' label='What type of list?' isRequired={false}>
                    <RadioGroup
                        name='buttons'
                        selectedValue={listType}
                        // @ts-expect-error
                        onChange={(newVal) => setListType(newVal)}
                    >
                        <div
                            data-tip={
                                props.navItem.type === 'audience'
                                    ? 'Once created, the type of an audience cannot be changed'
                                    : undefined
                            }
                            data-place='bottom'
                        >
                            <RadioButton
                                value='list'
                                key='list'
                                disabled={props.navItem.type === 'audience'}
                            >
                                <StackLegacy spacing={4}>
                                    <Body>This list is dynamic</Body>
                                    <Body size='small' color='light'>
                                        People in this list will update automatically based on your
                                        selected filters.
                                    </Body>
                                </StackLegacy>
                            </RadioButton>
                        </div>
                        <RadioButton
                            value='audience'
                            key='audience'
                            disabled={!props.canAccessMarketing}
                        >
                            <StackLegacy spacing={4}>
                                <div className='flex gap-8 align-center'>
                                    <Body>This is a fixed audience</Body>
                                    {!props.canAccessMarketing && (
                                        <UpgradeArrowIcon
                                            size={16}
                                            data-tip='Add a Marketing plan to use this feature'
                                            data-place='bottom'
                                            className='flex align-center'
                                        />
                                    )}
                                </div>
                                <Body size='small' color='light'>
                                    {props.navItem.count
                                        ? props.navItem.count.value
                                        : 'All filtered'}{' '}
                                    people will be added to a new list and can only be manually
                                    removed.
                                </Body>
                            </StackLegacy>
                        </RadioButton>
                    </RadioGroup>
                </FormField>
            )}
            {!NutClientConfig.hasSharedListsFF && (
                <FormField innerId='isShared' isRequired={false}>
                    <div
                        data-tip={
                            listType === 'audience'
                                ? 'Audiences are always shared with your team'
                                : undefined
                        }
                        data-place='bottom'
                        className='width-fit-content'
                    >
                        <Checkbox
                            // @ts-expect-error
                            id='isShared'
                            name='isShared'
                            checked={isShared || listType === 'audience'}
                            disabled={listType === 'audience'}
                            onChange={(val) => {
                                setIsShared(val);
                            }}
                            label='Share this with everyone'
                        />
                    </div>
                </FormField>
            )}
            <div className='flex justify-sb pb-32'>
                <Button variant='text' size='big' onClick={props.onClose}>
                    Cancel
                </Button>
                <div className='flex gap-8'>
                    <SaveButton
                        isSaving={props.isCopying}
                        size='big'
                        disabled={!name || !props.navItem.id}
                        // @ts-expect-error
                        onClick={onCopy}
                        buttonText='Save as a copy'
                        savingText='Saving...'
                        successText='Saved'
                        variant='secondary'
                    />
                    <SaveButton
                        isSaving={props.isSaving}
                        size='big'
                        disabled={
                            !name ||
                            props.navItem.isImmutable ||
                            (props.navItem.type !== listType && Boolean(props.navItem.id))
                        }
                        // @ts-expect-error
                        onClick={onSave}
                        buttonText={props.navItem.id ? 'Save changes' : 'Save'}
                        savingText='Saving...'
                        successText='Saved'
                    />
                </div>
            </div>
        </>
    );
}
